
import React, { useRef, useState, useCallback } from 'react';
import { Node, Connection, Position, NodeType } from '../types';
import { NODE_CONFIGS } from '../constants';
import { 
  LLMNode, MCPNode, IntegrationNode, DataConnectorNode,
  StartNode, EndNode, MemoryNode, AgentEngineNode, EvaluationNode, TriggerNode,
  Icons, ChevronUp, TrashIcon 
} from './NodeComponents';
import { TestModal } from './TestModal';

interface CanvasProps {
  nodes: Node[];
  connections: Connection[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setConnections: React.Dispatch<React.SetStateAction<Connection[]>>;
  onNodeSelect: (id: string | null) => void;
  selectedNodeId: string | null;
}

const NodeRenderer = ({ type, data, onChange, nodeId, nodes, connections }: { type: NodeType, data: any, onChange: (d: any) => void, nodeId?: string, nodes?: Node[], connections?: Connection[] }) => {
  switch (type) {
    case 'start': return <StartNode />;
    case 'end': return <EndNode />;
    case 'memory': return <MemoryNode data={data} onChange={onChange} />;
    case 'agent_engine': return <AgentEngineNode data={data} onChange={onChange} />;
    case 'llm': return <LLMNode data={data} onChange={onChange} />;
    case 'mcp': return <MCPNode data={data} onChange={onChange} />;
    case 'integration': return <IntegrationNode data={data} onChange={onChange} />;
    case 'data_connector': return <DataConnectorNode data={data} onChange={onChange} />;
    case 'evaluation': return <EvaluationNode data={data} onChange={onChange} nodeId={nodeId} nodes={nodes} connections={connections} />;
    case 'trigger': return <TriggerNode data={data} onChange={onChange} />;
    default: return <div>Unknown Node</div>;
  }
};

export const Canvas: React.FC<CanvasProps> = ({ nodes, connections, setNodes, setConnections, onNodeSelect, selectedNodeId }) => {
  const [view, setView] = useState({ x: 0, y: 0, zoom: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [draggingNode, setDraggingNode] = useState<{ id: string, offsetX: number, offsetY: number } | null>(null);
  const [hasDragged, setHasDragged] = useState(false); // Track if actual drag movement occurred
  const [connecting, setConnecting] = useState<{ sourceId: string, sourcePos: Position, type: NodeType } | null>(null);
  const [mousePos, setMousePos] = useState<Position>({ x: 0, y: 0 });
  const [hoveredConnection, setHoveredConnection] = useState<string | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Dimensions
  const AGENT_WIDTH = 280;
  const SUB_NODE_SIZE = 64; // Circle diameter
  const STANDARD_WIDTH = 220;
  const AGENT_HEADER_HEIGHT = 80;
  // Estimated height of the expanded content for Agent Engine to align connections
  const AGENT_BODY_HEIGHT = 450; 

  const getCanvasCoordinates = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - view.x) / view.zoom,
      y: (clientY - rect.top - view.y) / view.zoom
    };
  }, [view.x, view.y, view.zoom]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.button === 0 && e.target === canvasRef.current) || (e.target as HTMLElement).tagName === 'svg') {
      setIsPanning(true);
      onNodeSelect(null);
    }
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setView(prev => ({
        ...prev,
        x: prev.x + e.movementX,
        y: prev.y + e.movementY
      }));
      return;
    }

    const worldPos = getCanvasCoordinates(e.clientX, e.clientY);
    
    // Only update mouse position when actively connecting (reduces re-renders)
    if (connecting) {
      setMousePos(worldPos);
    }

    if (draggingNode) {
      setHasDragged(true); // Mark that actual dragging occurred
      setNodes(nds => nds.map(n => {
        if (n.id === draggingNode.id) {
          return {
            ...n,
            position: {
              x: worldPos.x - draggingNode.offsetX,
              y: worldPos.y - draggingNode.offsetY
            }
          };
        }
        return n;
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPanning, connecting, draggingNode, view, getCanvasCoordinates]);

  const handleMouseUp = () => {
    setIsPanning(false);
    setDraggingNode(null);
    setHasDragged(false); // Reset drag tracking
    setConnecting(null);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = -e.deltaY * 0.001;
      const newZoom = Math.max(0.2, Math.min(2, view.zoom + zoomFactor));
      setView(prev => ({ ...prev, zoom: newZoom }));
    } else {
      setView(prev => ({
        ...prev,
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY
      }));
    }
  };

  const handleNodeMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onNodeSelect(id);
    const worldPos = getCanvasCoordinates(e.clientX, e.clientY);
    const node = nodes.find(n => n.id === id);
    if (node) {
      setDraggingNode({
        id,
        offsetX: worldPos.x - node.position.x,
        offsetY: worldPos.y - node.position.y
      });
    }
  };

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setNodes(nds => nds.map(n => n.id === id ? { ...n, expanded: !n.expanded } : n));
  };

  // Logic update: 'integration' is now a full node, not a sub-node
  const isSubNode = (type: NodeType) => ['llm', 'memory', 'mcp'].includes(type);

  // --- PORT LOGIC ---

  const handlePortDown = (e: React.MouseEvent, nodeId: string, type: NodeType) => {
    e.stopPropagation();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    let sourcePos = { x: 0, y: 0 };

    if (isSubNode(type)) {
      // Sub-nodes output from TOP
      sourcePos = {
        x: node.position.x + (node.expanded ? STANDARD_WIDTH / 2 : SUB_NODE_SIZE / 2),
        y: node.position.y
      };
    } else {
      // Standard nodes output from RIGHT
      // Agent Engine, Tool Agents (integration), Evaluation, and Trigger are wider
      const width = (node.type === 'agent_engine' || node.type === 'integration' || node.type === 'evaluation' || node.type === 'trigger') ? AGENT_WIDTH : STANDARD_WIDTH;
      sourcePos = {
        x: node.position.x + width,
        y: node.position.y + 40 // Approx half height
      };
    }

    setConnecting({ sourceId: nodeId, sourcePos, type });
  };

  const handlePortUp = (e: React.MouseEvent, targetId: string, type: NodeType) => {
    e.stopPropagation();
    if (connecting && connecting.sourceId !== targetId) {
      const exists = connections.some(c => c.source === connecting.sourceId && c.target === targetId);
      if (!exists) {
        setConnections(prev => [...prev, {
          id: `c_${Date.now()}`,
          source: connecting.sourceId,
          target: targetId,
          sourceType: connecting.type,
          targetType: type
        }]);
      }
    }
    setConnecting(null);
  };

  const deleteConnection = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setConnections(conns => conns.filter(c => c.id !== id));
  };

  // --- PATH DRAWING ---

  const getPath = (sx: number, sy: number, tx: number, ty: number, sourceType: NodeType, targetType: NodeType) => {
    // If connecting a Sub-Node (source) to an Agent or Tool Agent (target) -> Vertical Curve
    if (isSubNode(sourceType) && (targetType === 'agent_engine' || targetType === 'integration')) {
       const controlYSource = sy - 50;
       const controlYTarget = ty + 50;
       return `M ${sx} ${sy} C ${sx} ${controlYSource}, ${tx} ${controlYTarget}, ${tx} ${ty}`;
    }
    
    // Default Horizontal Curve
    const deltaX = Math.abs(tx - sx);
    const controlX = Math.max(deltaX * 0.5, 60); 
    return `M ${sx} ${sy} C ${sx + controlX} ${sy}, ${tx - controlX} ${ty}, ${tx} ${ty}`;
  };

  const getBezierMidpoint = (sx: number, sy: number, tx: number, ty: number, sourceType: NodeType, targetType: NodeType) => {
    let cp1x, cp1y, cp2x, cp2y;

    if (isSubNode(sourceType) && (targetType === 'agent_engine' || targetType === 'integration')) {
       // Vertical Logic
       cp1x = sx; cp1y = sy - 50;
       cp2x = tx; cp2y = ty + 50;
    } else {
       // Horizontal Logic
       const deltaX = Math.abs(tx - sx);
       const controlX = Math.max(deltaX * 0.5, 60);
       cp1x = sx + controlX; cp1y = sy;
       cp2x = tx - controlX; cp2y = ty;
    }

    const t = 0.5;
    const x = Math.pow(1-t, 3)*sx + 3*Math.pow(1-t, 2)*t*cp1x + 3*(1-t)*Math.pow(t, 2)*cp2x + Math.pow(t, 3)*tx;
    const y = Math.pow(1-t, 3)*sy + 3*Math.pow(1-t, 2)*t*cp1y + 3*(1-t)*Math.pow(t, 2)*cp2y + Math.pow(t, 3)*ty;
    return { x, y };
  };

  const updateNodeData = (id: string, newData: any) => {
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, ...newData } } : n));
  };

  const deleteNode = () => {
    if (selectedNodeId) {
      setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
      setConnections(conns => conns.filter(c => c.source !== selectedNodeId && c.target !== selectedNodeId));
      onNodeSelect(null);
    }
  };

  return (
    <div 
      ref={canvasRef}
      className={`w-full h-full bg-grid-pattern relative overflow-hidden adk-canvas ${isPanning ? 'is-panning cursor-grabbing' : 'cursor-grab'} ${draggingNode ? 'is-dragging' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <div 
        className="absolute top-0 left-0 w-full h-full origin-top-left will-change-transform"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
      >
        <svg className="absolute overflow-visible pointer-events-none z-0">
          <defs>
             <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
               <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
             </marker>
             <marker id="arrowhead-hover" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
               <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
             </marker>
          </defs>
          
          {connections.map(conn => {
            const source = nodes.find(n => n.id === conn.source);
            const target = nodes.find(n => n.id === conn.target);
            if (!source || !target) return null;

            // Calculate Source Coords
            let sx, sy;
            if (isSubNode(conn.sourceType)) {
                // Sub-node output is Top Center
                sx = source.position.x + (source.expanded ? STANDARD_WIDTH / 2 : SUB_NODE_SIZE / 2);
                sy = source.position.y;
            } else {
                // Standard output is Right Center
                const width = (conn.sourceType === 'agent_engine' || conn.sourceType === 'integration' || conn.sourceType === 'evaluation' || conn.sourceType === 'trigger') ? AGENT_WIDTH : STANDARD_WIDTH;
                sx = source.position.x + width;
                sy = source.position.y + 40;
            }

            // Calculate Target Coords
            let tx, ty;
            const isSubConnection = (conn.targetType === 'agent_engine' || conn.targetType === 'integration') && isSubNode(conn.sourceType);
            
            if (isSubConnection) {
                // Sub nodes connecting INTO Agent Engine or Tool Agent bottom ports
                const currentHeight = target.expanded ? (AGENT_HEADER_HEIGHT + AGENT_BODY_HEIGHT) : AGENT_HEADER_HEIGHT;
                ty = target.position.y + currentHeight + 12;

                if (conn.sourceType === 'llm') {
                    tx = target.position.x + 38;
                } else if (conn.sourceType === 'memory') {
                    tx = target.position.x + 140;
                } else {
                    // mcp or others
                    tx = target.position.x + 242;
                }
            } else {
                // Standard Input Left Center
                tx = target.position.x;
                ty = target.position.y + 40;
            }

            const isHovered = hoveredConnection === conn.id;
            const midpoint = getBezierMidpoint(sx, sy, tx, ty, conn.sourceType, conn.targetType);

            return (
              <g 
                key={conn.id} 
                onMouseEnter={() => setHoveredConnection(conn.id)}
                onMouseLeave={() => setHoveredConnection(null)}
                className="pointer-events-auto cursor-pointer"
              >
                {/* Hit area */}
                <path d={getPath(sx, sy, tx, ty, conn.sourceType, conn.targetType)} stroke="transparent" strokeWidth="20" fill="none" />
                
                {/* Visible line */}
                <path 
                  d={getPath(sx, sy, tx, ty, conn.sourceType, conn.targetType)} 
                  stroke={isHovered ? "#ef4444" : (isSubConnection ? "#94a3b8" : "#cbd5e1")} 
                  strokeWidth={isHovered ? "3" : "2"} 
                  fill="none" 
                  strokeDasharray={isSubConnection ? "5,5" : "none"}
                  markerEnd={isHovered ? "url(#arrowhead-hover)" : "url(#arrowhead)"}
                  className={!isSubConnection ? "connection-line" : ""}
                />

                {/* Delete Button (visible on hover) */}
                {isHovered && (
                    <g 
                        transform={`translate(${midpoint.x}, ${midpoint.y})`} 
                        onClick={(e) => deleteConnection(e, conn.id)}
                        className="cursor-pointer"
                    >
                        <circle r="10" fill="#ef4444" />
                        <line x1="-4" y1="-4" x2="4" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        <line x1="4" y1="-4" x2="-4" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </g>
                )}
              </g>
            );
          })}

          {connecting && (
             <path 
               d={getPath(connecting.sourcePos.x, connecting.sourcePos.y, mousePos.x, mousePos.y, connecting.type, 'start')} // dummy targetType
               stroke="#3b82f6" 
               strokeWidth="2" 
               strokeDasharray="5,5"
               fill="none" 
               markerEnd="url(#arrowhead)"
             />
          )}
        </svg>

        {nodes.map(node => {
           const config = NODE_CONFIGS[node.type];
           const isSelected = selectedNodeId === node.id;
           const isDragging = draggingNode?.id === node.id;
           const isSub = isSubNode(node.type);
           const Icon = Icons[config.icon as keyof typeof Icons];
           
           // Determine dimensions based on type and state
           const width = (node.type === 'agent_engine' || node.type === 'integration' || node.type === 'evaluation' || node.type === 'trigger') ? AGENT_WIDTH : (isSub && !node.expanded ? SUB_NODE_SIZE : STANDARD_WIDTH);
           
           // Dynamic Title & Subtitle Styling
           let titleText = config.title;
           let subtitleText = 'Configured';
           const isAgentOrMarketplace = node.type === 'agent_engine' || node.type === 'integration' || node.type === 'evaluation' || node.type === 'trigger';

           if (node.type === 'agent_engine') {
               titleText = node.data.agentName || 'New Agent';
               subtitleText = 'Agent Engine';
           } else if (node.type === 'integration') {
               titleText = node.data.agentName || 'Agent Marketplace';
               subtitleText = 'Agent Marketplace';
           } else if (node.type === 'trigger') {
               titleText = 'Integration Trigger';
               const platformList = Array.isArray(node.data.triggerPlatforms) 
                   ? node.data.triggerPlatforms 
                   : (node.data.triggerPlatform ? [node.data.triggerPlatform] : ['whatsapp']);
               subtitleText = platformList.includes('google_drive') 
                   ? 'Google Drive Folder' 
                   : (platformList.includes('telegram') ? 'Telegram Bot' : 'WhatsApp Business');
           } else if (node.type === 'start') {
               subtitleText = 'Initial Trigger';
           } else if (node.type === 'end') {
               subtitleText = 'Final Output';
           }

           // Render Sub-Node (Circular Style)
           if (isSub && !node.expanded) {
               return (
                <div
                    key={node.id}
                    className={`absolute flex flex-col items-center group
                      ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
                    `}
                    style={{ 
                        left: node.position.x, 
                        top: node.position.y,
                        width: SUB_NODE_SIZE,
                        zIndex: isSelected || isDragging ? 50 : 20,
                    }}
                    onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                >
                    {/* Output Port (Top) */}
                    <div 
                        className="absolute -top-2 w-3 h-3 bg-slate-200 border border-slate-400 rounded-full hover:bg-blue-500 hover:border-blue-600 transition-all z-20 cursor-crosshair opacity-0 group-hover:opacity-100"
                        onMouseDown={(e) => handlePortDown(e, node.id, node.type)}
                    ></div>

                    {/* Circular Body */}
                    <div 
                        onClick={(e) => { if(!hasDragged) toggleExpand(e, node.id); }}
                        className={`w-16 h-16 rounded-full bg-white border-2 flex items-center justify-center shadow-md transition-all hover:scale-105 hover:shadow-lg
                            ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-400'}
                        `}
                    >
                        <div style={{ color: config.accent }}>
                            <Icon />
                        </div>
                    </div>
                    
                    {/* Label */}
                    <div className="mt-2 text-center pointer-events-none">
                        <span className="block text-[10px] font-bold text-slate-700 leading-tight bg-white/80 px-2 py-0.5 rounded-full backdrop-blur-sm border border-slate-100">
                             {config.title}
                        </span>
                        <span className="text-[9px] text-slate-400">
                            {node.type === 'llm' && (node.data.modelProvider || 'Select Model')}
                            {node.type === 'memory' && (node.data.memoryType || 'Buffer')}
                        </span>
                    </div>
                </div>
               );
           }

           // Render Standard Card Node
           return (
             <div
                key={node.id}
                className={`absolute w-[${width}px] rounded-lg flex flex-col bg-white
                  ${isSelected ? 'shadow-node-selected ring-1 ring-blue-500' : 'shadow-node hover:shadow-node-hover'}
                  ${isDragging ? 'cursor-grabbing' : ''}
                `}
                style={{ 
                    left: node.position.x, 
                    top: node.position.y,
                    width: width,
                    zIndex: isSelected || isDragging ? 50 : 10,
                    overflow: 'visible' 
                }}
             >
                <div className={`relative bg-white rounded-lg border border-slate-200 overflow-visible transition-all duration-200`}>
                    <div 
                        className={`px-4 py-3 flex items-center justify-between cursor-grab active:cursor-grabbing bg-white group hover:bg-slate-50 transition-colors rounded-t-lg ${!node.expanded ? 'rounded-b-lg' : ''}`}
                        style={{ height: AGENT_HEADER_HEIGHT }}
                        onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                        onClick={(e) => { if(!hasDragged) toggleExpand(e, node.id); }}
                    >
                        <div className="flex items-center gap-3 w-full">
                            <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border border-slate-100 shadow-sm" 
                                style={{ backgroundColor: isAgentOrMarketplace ? '#111827' : `${config.accent}15` }}
                            >
                                <div style={{ color: isAgentOrMarketplace ? '#ffffff' : config.accent }}>
                                    <Icon />
                                </div>
                            </div>
                            
                            <div className="flex flex-col min-w-0">
                                {/* Title styling for Agent/Marketplace: Black Bold */}
                                <span className={`text-sm font-bold truncate ${isAgentOrMarketplace ? 'text-black' : 'text-slate-800'}`} title={titleText}>
                                    {titleText}
                                </span>
                                {/* Subtitle styling: Small Grey */}
                                <span className="text-[10px] text-slate-400 font-medium truncate">
                                    {subtitleText}
                                </span>
                            </div>
                        </div>
                        {isSub && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); toggleExpand(e, node.id); }}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <ChevronUp />
                            </button>
                        )}
                    </div>

                    {node.expanded && (
                        <div 
                          className="p-3 bg-white border-t border-slate-100 rounded-b-lg animate-fade-in" 
                          onMouseDown={(e) => e.stopPropagation()}
                          style={isAgentOrMarketplace ? { minHeight: AGENT_BODY_HEIGHT } : {}}
                        >
                            <NodeRenderer 
                                type={node.type} 
                                data={node.data} 
                                onChange={(d) => updateNodeData(node.id, d)}
                                nodeId={node.id}
                                nodes={nodes}
                                connections={connections}
                            />
                        </div>
                    )}
                </div>

                {/* --- PORTS --- */}

                {/* Sub-node Expanded Output (Top) */}
                {isSub && node.expanded && (
                    <div 
                        className="absolute -top-3 left-1/2 -translate-x-1/2 w-4 h-4 bg-slate-200 border border-slate-400 rounded-full hover:bg-blue-500 hover:scale-110 transition-all z-20 cursor-crosshair"
                        onMouseDown={(e) => handlePortDown(e, node.id, node.type)}
                    ></div>
                )}

                {/* Agent Engine & Agent Marketplace (Integration): Specific Named Input Ports (BOTTOM) */}
                {isAgentOrMarketplace && (
                  <div className="absolute -bottom-3 left-0 w-full flex justify-between px-8">
                    {/* Model Port */}
                    <div className="relative group/port">
                        <div 
                            className="w-3 h-3 bg-white border-2 border-indigo-500 rounded-full hover:scale-125 transition-transform cursor-crosshair z-30"
                            onMouseUp={(e) => handlePortUp(e, node.id, node.type)}
                            title="Connect Model"
                        ></div>
                        <span className="absolute top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-500 whitespace-nowrap opacity-0 group-hover/port:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-slate-100 pointer-events-none">Model</span>
                    </div>

                    {/* Memory Port */}
                    <div className="relative group/port">
                        <div 
                            className="w-3 h-3 bg-white border-2 border-pink-500 rounded-full hover:scale-125 transition-transform cursor-crosshair z-30"
                            onMouseUp={(e) => handlePortUp(e, node.id, node.type)}
                            title="Connect Memory"
                        ></div>
                         <span className="absolute top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-500 whitespace-nowrap opacity-0 group-hover/port:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-slate-100 pointer-events-none">Memory</span>
                    </div>

                    {/* Tools Port (Only for Agent Engine really, but kept for layout consistency or chaining) */}
                    {node.type === 'agent_engine' ? (
                        <div className="relative group/port">
                            <div 
                                className="w-3 h-3 bg-white border-2 border-orange-500 rounded-full hover:scale-125 transition-transform cursor-crosshair z-30"
                                onMouseUp={(e) => handlePortUp(e, node.id, node.type)}
                                title="Connect Tools"
                            ></div>
                            <span className="absolute top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-500 whitespace-nowrap opacity-0 group-hover/port:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-slate-100 pointer-events-none">Tools</span>
                        </div>
                    ) : (
                        <div className="w-3 h-3 invisible"></div>
                    )}
                  </div>
                )}
                
                {/* Standard Input Port (Left) - Only for Agent/End/Integration */}
                {!isSub && node.type !== 'start' && (
                    <div 
                        className="absolute w-3 h-3 bg-white border-[3px] border-slate-300 rounded-full -left-[6px] top-[40px] hover:border-blue-500 hover:scale-110 transition-transform z-20 cursor-crosshair shadow-sm"
                        onMouseUp={(e) => handlePortUp(e, node.id, node.type)}
                        title="Input"
                    ></div>
                )}
                
                {/* Standard Output Port (Right) - Only for Agent/Start/Integration */}
                {!isSub && node.type !== 'end' && (
                    <div 
                        className="absolute w-3 h-3 bg-white border-[3px] border-slate-300 rounded-full -right-[6px] top-[40px] hover:border-blue-500 hover:scale-110 transition-transform z-20 cursor-crosshair shadow-sm"
                        onMouseDown={(e) => handlePortDown(e, node.id, node.type)}
                        title="Output"
                    ></div>
                )}
             </div>
           );
        })}
      </div>

      {/* Floating Action Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-50">
         <button 
           className="px-6 py-2.5 bg-[#931508] hover:bg-[#7a1207] text-white rounded-md shadow-lg flex items-center gap-2 font-semibold text-sm transition-colors"
           onClick={() => setShowTestModal(true)}
         >
           <Icons.Flask />
           Test workflow
         </button>
         
         <button 
           className={`p-2.5 rounded-md shadow-lg transition-colors flex items-center justify-center ${selectedNodeId ? 'bg-[#931508] hover:bg-[#7a1207] text-white cursor-pointer' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
           onClick={deleteNode}
           disabled={!selectedNodeId}
         >
           <TrashIcon />
         </button>
      </div>

      {/* Zoom Controls */}
      <div className="absolute bottom-6 left-6 flex gap-2 z-50">
          <div className="flex bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
             <button className="p-2 hover:bg-gray-50 text-gray-600 border-r border-gray-100" onClick={() => setView(v => ({...v, zoom: v.zoom + 0.1}))}>
               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
             </button>
             <div className="px-3 py-2 text-xs font-mono text-gray-500 min-w-[3rem] text-center flex items-center justify-center bg-gray-50">
               {Math.round(view.zoom * 100)}%
             </div>
             <button className="p-2 hover:bg-gray-50 text-gray-600" onClick={() => setView(v => ({...v, zoom: v.zoom - 0.1}))}>
               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
             </button>
          </div>
          <button 
             className="bg-white p-2 rounded-lg shadow-lg border border-gray-100 hover:bg-gray-50 text-gray-600"
             onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
             title="Fit to Screen"
          >
             <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
          </button>
      </div>

      <TestModal 
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        nodes={nodes}
        connections={connections}
        selectedNodeId={selectedNodeId}
      />
    </div>
  );
};
